import { useEffect, useRef, useSyncExternalStore } from "react";
import { io, type Socket } from "socket.io-client";
import { refreshSession, useAuth } from "@/features/auth/store";
import { queryClient } from "@/shared/api/query-client";

export type RtEvent<T = unknown> = { id: string; type: string; branchId: string | null; occurredAt: string; version: number | null; entity: { type: string; id: string }; data: T };
type Status = "connecting" | "online" | "offline";

let socket: Socket | null = null;
let key = "";
let status: Status = "offline";
const listeners = new Set<() => void>();
const setStatus = (s: Status) => {
  status = s;
  listeners.forEach((l) => l());
};
/** Комнаты, которые нужно восстановить после переподключения. */
const joined = new Map<string, { room: "kitchen" | "expo"; stationId?: string }>();

/**
 * Одно соединение Socket.IO на сессию (ТЗ 11): auth { token, branchId }. WebSocket — канал уведомлений:
 * после reconnect все запросы перезапрашиваются (сверка по REST, §5.4).
 */
function ensure() {
  const { accessToken, branchId, status: auth } = useAuth.getState();
  const nextKey = auth === "authenticated" && accessToken ? `${branchId}` : "";
  if (nextKey === key && socket) return;
  socket?.disconnect();
  socket = null;
  key = nextKey;
  if (!nextKey) return setStatus("offline");

  setStatus("connecting");
  const s = io("/rt", {
    path: "/ws",
    transports: ["websocket", "polling"],
    auth: (cb) => cb({ token: useAuth.getState().accessToken, branchId: useAuth.getState().branchId }),
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
  });
  let wasOnline = false;
  s.on("ready", () => {
    setStatus("online");
    for (const r of joined.values()) s.emit("room.join", r);
    if (wasOnline) void queryClient.invalidateQueries();
    wasOnline = true;
  });
  s.on("auth.error", async () => {
    setStatus("connecting");
    // access истёк — обновляем и переподключаемся (auth берётся заново при connect)
    if (await refreshSession()) setTimeout(() => s.connect(), 200);
    else setStatus("offline");
  });
  s.on("disconnect", () => setStatus(s.active ? "connecting" : "offline"));
  socket = s;
}

useAuth.subscribe(ensure);

export function useRealtimeStatus(): Status {
  return useSyncExternalStore((cb) => (listeners.add(cb), () => listeners.delete(cb)), () => status);
}

/** Подписка на событие; обработчик всегда актуальный (без переподписки при рендере). */
export function useRealtime<T>(type: string | string[], handler: (e: RtEvent<T>) => void) {
  const ref = useRef(handler);
  ref.current = handler;
  const types = Array.isArray(type) ? type : [type];
  const sig = types.join(",");
  useEffect(() => {
    ensure();
    const s = socket;
    if (!s) return;
    const fn = (e: RtEvent<T>) => ref.current(e);
    for (const t of types) s.on(t, fn);
    return () => {
      for (const t of types) s.off(t, fn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, key]);
}

/** Комната KDS-станции / expo. */
export function useRoom(room: "kitchen" | "expo", stationId?: string | null) {
  useEffect(() => {
    if (room === "kitchen" && !stationId) return;
    ensure();
    const id = `${room}:${stationId ?? ""}`;
    const payload = { room, ...(stationId && { stationId }) };
    joined.set(id, payload);
    socket?.emit("room.join", payload);
    return () => {
      joined.delete(id);
      socket?.emit("room.leave", payload);
    };
  }, [room, stationId]);
}
